import { useCallback, useEffect, useMemo, useState } from 'react';
import { userProfileService } from '../services/userProfileService';
import { appendQueryParam } from '../utils/avatarCache';

const normalizeUserId = (userId) => {
    if (userId === null || userId === undefined) {
        return null;
    }
    const trimmed = String(userId).trim();
    if (!trimmed || trimmed.toLowerCase() === 'undefined') {
        return null;
    }
    return trimmed;
};

export const useProfileCover = (userId, options = {}) => {
    const normalizedUserId = useMemo(() => normalizeUserId(userId), [userId]);
    const enabled = options.enabled !== false;
    const [showProfileCoverUpload, setShowProfileCoverUpload] = useState(false);
    const [rawProfileCover, setRawProfileCover] = useState(null);
    const [coverVersion, setCoverVersion] = useState(0);

    const setProfileCover = useCallback((nextUrl) => {
        setRawProfileCover(nextUrl || null);
        setCoverVersion((current) => (nextUrl ? current + 1 : 0));
    }, []);

    const refreshProfileCover = useCallback(async () => {
        if (!enabled || !normalizedUserId) {
            setProfileCover(null);
            return null;
        }
        const response = await userProfileService.getProfileCoverUrl(normalizedUserId);
        const url = response?.url || null;
        setProfileCover(url);
        return url;
    }, [enabled, normalizedUserId, setProfileCover]);

    useEffect(() => {
        let cancelled = false;
        if (!enabled || !normalizedUserId) {
            setProfileCover(null);
            return undefined;
        }
        userProfileService
            .getProfileCoverUrl(normalizedUserId)
            .then((response) => {
                if (!cancelled) {
                    setProfileCover(response?.url || null);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error('Failed to fetch profile cover', error);
                    setProfileCover(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [enabled, normalizedUserId, setProfileCover]);

    const profileCover = useMemo(() => {
        if (!rawProfileCover) {
            return null;
        }
        return coverVersion > 0
            ? appendQueryParam(rawProfileCover, 'v', coverVersion)
            : rawProfileCover;
    }, [coverVersion, rawProfileCover]);

    return {
        showProfileCoverUpload,
        setShowProfileCoverUpload,
        profileCover,
        rawProfileCover,
        setProfileCover,
        handleProfileCoverUpdate: setProfileCover,
        refreshProfileCover,
    };
};

export default useProfileCover;
